// Owns the transforms that relate the compared slides. CompareView keeps the
// engines and the layout; how the slides sit relative to each other lives here.
//
// The panes navigate independently until the lock is engaged — you line them up
// yourself, then Lock freezes the relationships and every follower is derived
// from whichever pane you are driving through these transforms.

import { useCallback, useMemo, useRef, type MutableRefObject } from 'react';
import { fromViewPair, invert, type SimilarityTransform } from '../../viewer/align/transform';
import type { ViewportEngine } from '../../viewer/ViewportEngine';

type Panes = readonly (string | undefined)[];
type Engines = MutableRefObject<(ViewportEngine | null)[]>;

export function useAlignment({
  engines,
  paneSeriesRef,
}: {
  engines: Engines;
  paneSeriesRef: MutableRefObject<Panes>;
}) {
  // Keyed by series UID, every ordered pair stored. Not a single A→B value: any
  // pane can be the one you are driving, so the loop needs i→j for whichever i
  // moved, and keying by UID means swapping panes around keeps the alignment.
  const alignment = useRef<Map<string, SimilarityTransform>>(new Map());

  const keyFor = useCallback(
    (leaderUID: string | undefined, followerUID: string | undefined) =>
      leaderUID && followerUID ? `${leaderUID}→${followerUID}` : null,
    [],
  );

  const transformFor = useCallback(
    (leaderUID: string | undefined, followerUID: string | undefined) => {
      const k = keyFor(leaderUID, followerUID);
      return k ? alignment.current.get(k) : undefined;
    },
    [keyFor],
  );

  /** Freeze the panes' current relative poses as the relationships — what Lock
   *  means: "keep the offsets I have just lined up". Pairs whose engines are not
   *  ready yet are skipped. Without `force`, a pair already set is kept. */
  const establish = useCallback(
    (force = false): boolean => {
      const panes = paneSeriesRef.current;
      let any = false;
      for (let i = 0; i < panes.length; i++) {
        for (let j = i + 1; j < panes.length; j++) {
          // The same slide in two panes makes both directions the same key, so
          // the second write would overwrite the first with its own inverse and
          // the follower would track backwards. There is nothing to relate
          // between a slide and itself anyway.
          if (panes[i] === panes[j]) continue;
          const ij = keyFor(panes[i], panes[j]);
          const ji = keyFor(panes[j], panes[i]);
          if (!ij || !ji) continue;
          if (!force && alignment.current.has(ij)) {
            any = true;
            continue;
          }
          const gi = engines.current[i]?.getViewGeometry();
          const gj = engines.current[j]?.getViewGeometry();
          if (!gi || !gj) continue;
          const t = fromViewPair(gi, gj);
          alignment.current.set(ij, t);
          alignment.current.set(ji, invert(t));
          any = true;
        }
      }
      return any;
    },
    [engines, keyFor, paneSeriesRef],
  );

  // Memoised: this object lands in the dependency list of CompareView's effects,
  // and a fresh identity every render would re-run them on every render — which
  // re-captured the lock relationship continuously, quietly defeating the lock.
  return useMemo(() => ({ transformFor, establish }), [transformFor, establish]);
}
